import { useEffect, useMemo } from "react"
import { useSortedModels, type Model } from "resources/models"
import { usePortfolios, type Portfolio } from "resources/portfolios"
import { combineStatuses, OK } from "helpers/data-statuses"
import { getMostUsedOrDefaultModelId } from "helpers/models"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "connected-components/dashboard/context/dashboard-id"
import { useCoreActions } from "../../hooks/use-core-actions"
import ModelSelectorAutocomplete from "connected-components/model-selector-autocomplete"

export default function ModelSelector({ disabled = false, onModelChange }: ModelSelectorProps) {
  const dashboardId = useDashboardId()
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)
  const { setModelId } = useCoreActions()
  const [portfolios, portfoliosStatus] = usePortfolios()
  const [models, modelsStatus] = useSortedModels()

  const status = useMemo(
    () => combineStatuses([modelsStatus, portfoliosStatus]),
    [modelsStatus, portfoliosStatus],
  )

  useEffect(
    () => {
      if (modelsStatus === OK && portfoliosStatus === OK && !modelId) {
        const defaultModelId = getMostUsedOrDefaultModelId(portfolios as Portfolio[], models as Model[])
        if (defaultModelId) {
          setModelId(defaultModelId)
        }
      }
    },
    [modelId, setModelId, portfolios, models, modelsStatus, portfoliosStatus],
  )

  const handleModelChange = onModelChange || setModelId

  if (status !== OK || !models) {
    return <div>Loading</div>
  }

  return <ModelSelectorAutocomplete
    models={models}
    value={modelId || ""}
    onChange={handleModelChange}
    disabled={disabled}
    popperPlacement="bottom-end"
  />
}

interface ModelSelectorProps {
  disabled?: boolean
  onModelChange?: (modelId: string) => void
}
